import {LineItemModel} from '../../../../domain/models/checkout/LineItems/LineItem.model';
import {ILocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {trimToMaxLength} from '../../../../domain/utils/trimToMaxLength';
import {getPaymentTypeLabel} from './LineItem.utils';
import {MAX_DESCRIPTION_LINE_LENGTH, MAX_MODIFIER_LENGTH} from '../../../../common/constants';
import {DescriptionLineModel} from '../../../../domain/models/checkout/LineItems/DescriptionLine.model';
import {ModifierModel} from '../../../../domain/models/checkout/LineItems/Modifier.model';
import {PaymentOptionType} from '../../../../domain/models/checkout/LineItems/PaymentConfig.model';

const getQuantityDescriptionLine = (lineItem: LineItemModel, localeKeys: ILocaleKeys): string[] => {
  const hideQuantity = lineItem.quantityInfo.fixedQuantity;

  if (!hideQuantity) {
    return [`${localeKeys.checkout.order_summary.items.quantity()}: ${lineItem.quantityInfo.quantity}`];
  }
  return [];
};

const getPaymentDescriptionLine = (lineItem: LineItemModel, localeKeys: ILocaleKeys): string[] => {
  if (lineItem.paymentConfig.paymentOption !== PaymentOptionType.FULL_PAYMENT_ONLINE) {
    return [`${trimToMaxLength(getPaymentTypeLabel(lineItem, localeKeys), MAX_DESCRIPTION_LINE_LENGTH)}`];
  }
  return [];
};

const getSKUDescriptionLine = (lineItem: LineItemModel, localeKeys: ILocaleKeys): string[] => {
  if (lineItem.attributes.physicalProperties.sku) {
    return [`${localeKeys.checkout.order_summary.items.sku()}: ${lineItem.attributes.physicalProperties.sku}`];
  }
  return [];
};

export const mapLineItemToDetails = (
  lineItem: LineItemModel,
  localeKeys: ILocaleKeys,
  checkoutUIRevampOrderSummary: boolean
): {
  descriptionLines?: string[];
  groups?: {descriptionLines: string[]; groupName?: string}[];
  screenReaderOnlyQuantityText?: string;
} => {
  const descriptionOptions: string[] = [
    ...getPaymentDescriptionLine(lineItem, localeKeys),
    ...(checkoutUIRevampOrderSummary ? [] : getQuantityDescriptionLine(lineItem, localeKeys)),
    ...getSKUDescriptionLine(lineItem, localeKeys),
    ...lineItem.attributes.descriptionLines.map(toDescriptionLineString),
  ];

  const modifierGroups = lineItem.modifierGroups;
  const hasOnlyOneSimpleGroup =
    modifierGroups?.length === 1 && !modifierGroups[0].name && modifierGroups[0].modifiers.length === 1;

  const screenReaderOnlyQuantityText =
    checkoutUIRevampOrderSummary && !lineItem.quantityInfo.fixedQuantity
      ? `${localeKeys.checkout.order_summary.items.quantity()}: ${lineItem.quantityInfo.quantity}`
      : undefined;

  let groups;
  if (hasOnlyOneSimpleGroup && descriptionOptions.length === 0) {
    descriptionOptions.push(toModifierString(lineItem.modifierGroups[0].modifiers[0], localeKeys));
  } else {
    groups = lineItem.modifierGroups.map((group) => {
      return {
        groupName: group.name,
        descriptionLines: group.modifiers.map((modifier) => toModifierString(modifier, localeKeys)),
        screenReaderOnlyQuantityText,
      };
    });
  }

  return {descriptionLines: descriptionOptions, groups, screenReaderOnlyQuantityText};
};

const toDescriptionLineString = (descriptionLine: DescriptionLineModel): string => {
  if (!descriptionLine.title) {
    return trimToMaxLength(descriptionLine.value, MAX_DESCRIPTION_LINE_LENGTH);
  }
  return `${descriptionLine.title}: ${trimToMaxLength(descriptionLine.value, MAX_DESCRIPTION_LINE_LENGTH)}`;
};

const toModifierString = (modifier: ModifierModel, localeKeys: ILocaleKeys): string => {
  let modifierName;
  if (!modifier.details) {
    modifierName = trimToMaxLength(modifier.label, MAX_MODIFIER_LENGTH);
  } else {
    modifierName = trimToMaxLength(`${modifier.label}: ${modifier.details}`, MAX_MODIFIER_LENGTH);
  }

  const modifierDetails = () => {
    if (modifier.quantity && modifier.price) {
      return 'quantityAndPrice';
    }
    if (modifier.quantity) {
      return 'quantity';
    }
    if (modifier.price) {
      return 'price';
    }
    return 'other';
  };

  return localeKeys.checkout.order_summary.items.modifierDetails({
    modifierDetails: modifierDetails(),
    quantity: modifier.quantity || undefined,
    price: modifier.price ? modifier.price.formattedAmount : undefined,
    modifierName,
  });
};
